import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, timer } from 'rxjs';

export interface DateWindow {
  from: number;
  to: number;
}

interface ZonedDateParts {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
}

@Injectable({
  providedIn: 'root',
})
export class DateTimeUtility {
  currentTime = new BehaviorSubject<any>(null);
  diffSub:Subscription;
  diffTimer = timer(1000, 1000);

  constructor() {
    this.createLiveCurrentTime();
  }

  createLiveCurrentTime() {
    this.diffSub = this.diffTimer.subscribe((res) => {
      this.currentTime.next(new Date().getTime());
    });
  }



  /**
   * Accepts time stamp and returns the time difference between timeStamp and current time in seconds
   *
   * @param timeStamp
   * @returns seconds
   */
  getTimeDifferenceInSeconds(timeStamp: number) {
    return ((Date.now() - timeStamp) / (1000 * 3600)) * 60 * 60;
  }

  /**
   *
   * @param seconds
   * @returns string, formatted second into h:mm:ss
   */
  formatSeconds(seconds: number) {
    return new Date(seconds * 1000).toISOString().substr(11, 8);
  }

  // format date in typescript
  getFormatedDate(date: Date, format: string) {
    const datePipe = new DatePipe('en-US');
    return datePipe.transform(date, format);
  }


  getCurrentDate(): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }

  getTimeSpanToDays(days: number): Date {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const calculatePreviousDate = new Date(today);
    calculatePreviousDate.setDate(calculatePreviousDate.getDate() + days);
    return calculatePreviousDate;
  }

  getCurrentStoreDateWindow(timeZone: string, leewayMs = 0, nowMs = Date.now()): DateWindow {
    const normalizedTimeZone = this.getValidTimeZone(timeZone);
    const today = this.getZonedDateParts(nowMs, normalizedTimeZone);
    const tomorrow = this.addDaysToDateParts(today, 1);
    const startOfToday = this.getZonedStartOfDayMs(today, normalizedTimeZone);
    const startOfTomorrow = this.getZonedStartOfDayMs(tomorrow, normalizedTimeZone);
    const from = leewayMs > 0 && nowMs < startOfToday + leewayMs
      ? startOfToday - leewayMs
      : startOfToday;

    return {
      from,
      to: startOfTomorrow,
    };
  }

  private getValidTimeZone(timeZone: string): string {
    const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    if (!timeZone) {
      return fallbackTimeZone;
    }

    try {
      new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
      return timeZone;
    } catch {
      return fallbackTimeZone;
    }
  }

  private getZonedDateParts(timestamp: number, timeZone: string): ZonedDateParts {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    return formatter.formatToParts(new Date(timestamp)).reduce((parts: ZonedDateParts, part) => {
      const value = Number(part.value);
      if (part.type === 'year') {
        parts.year = value;
      } else if (part.type === 'month') {
        parts.month = value;
      } else if (part.type === 'day') {
        parts.day = value;
      } else if (part.type === 'hour') {
        parts.hour = value;
      } else if (part.type === 'minute') {
        parts.minute = value;
      } else if (part.type === 'second') {
        parts.second = value;
      }
      return parts;
    }, { year: 0, month: 0, day: 0 });
  }

  private addDaysToDateParts(dateParts: ZonedDateParts, days: number): ZonedDateParts {
    const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day + days));
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  private getZonedStartOfDayMs(dateParts: ZonedDateParts, timeZone: string): number {
    const localMidnightAsUtc = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, 0, 0, 0, 0);
    let zonedMidnight = localMidnightAsUtc;

    for (let i = 0; i < 3; i++) {
      zonedMidnight = localMidnightAsUtc - this.getTimeZoneOffsetMs(zonedMidnight, timeZone);
    }

    return zonedMidnight;
  }

  private getTimeZoneOffsetMs(timestamp: number, timeZone: string): number {
    const parts = this.getZonedDateParts(timestamp, timeZone);
    const asUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour || 0,
      parts.minute || 0,
      parts.second || 0,
      0
    );

    return asUtc - timestamp;
  }
  
}
